import React, { useState } from "react";
import { Label } from "../Label";
import { PrimaryButton } from "../PrimaryButton";
import { TextInput } from "../TextInput";
import Icon from "../Icon2";
import { Cross } from "../Icons";
import { WaitingListPopupStyles as styles } from "./WaitingListPopup.styles";
// @ts-ignore
import {
  Session__DisplayDateTimeOffset,
  Session__DisplayDate,
} from "@obby/lib";

// Shared data + submit logic, so the widget and webcomponents wrappers don't
// duplicate the fetch/routing (they only differ in transport + rendering shell).
export {
  useWaitingListPopup,
  type WaitingListRequest,
  type WaitingListPopupData,
} from "./useWaitingListPopup";

// Hold on to submitted parameters if the user wants to
// sign up to more than one date in the same session.
// Shouldn't be stored in LocalStorage due to private nature.
let paramCache: WaitingListPopup.SubmitParams = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  numberOfGuests: 1,
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface CrossSellItem {
  key: string;
  label: React.ReactNode;
  meta?: React.ReactNode;
  onClick: () => void;
}

// Shared list for both cross-sell sections ("other dates" + "similar
// experiences"): identical card layout, differing only in title/intro and each
// item's label/meta/handler. Renders nothing when there are no items.
function CrossSellList({
  title,
  intro,
  items,
}: {
  title: string;
  intro: string;
  items: CrossSellItem[];
}) {
  if (items.length === 0) return null;
  return (
    <div className={styles.alternatives()}>
      <h4 className={styles.alternativesTitle()}>{title}</h4>
      <p className={styles.alternativesIntro()}>{intro}</p>
      <div className={styles.alternativesList()}>
        {items.map(item => (
          <button
            key={item.key}
            type="button"
            className={styles.alternativeItem()}
            onClick={item.onClick}
          >
            <span>{item.label}</span>
            {item.meta}
          </button>
        ))}
      </div>
    </div>
  );
}

export namespace WaitingListPopup {
  export interface SubmitParams {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    numberOfGuests: number;
    // Which waiting list to join: the specific session ("this date"), the
    // course-wide "any available date" list, or the whole "term". Defaults to
    // "session" (or "term" in term mode). Routed to the right endpoint by the
    // widget wrapper.
    target?: "session" | "course" | "term";
    // The session to join for a drop-in "this date" choice in term mode.
    sessionId?: string;
  }

  export interface AlternativeDate {
    sessionId: string;
    startDateTime: string;
    duration: number;
    isOnline: boolean;
    availability: number;
  }

  export interface SimilarExperience {
    courseId: string;
    title: string;
    slug: string;
    nextAvailableDate?: string;
  }

  // Term-mode data (Phase I.5). When present the popup joins a TERM's list.
  export interface Term {
    eventId: string;
    title: string;
    name?: string | null;
    startDateTime: string;
    endDateTime: string;
    numberOfSessions: number;
    // The term's sessions — used to show the chosen "this date" in the card.
    sessions?: {
      _id: string;
      startDateTime: string;
      duration?: number;
      isOnline?: boolean;
    }[];
  }

  export interface Props {
    session: {
      _id: string;
      location?: string;
      duration: number;
      date: string;
    } | null;
    course: any;
    location: any;
    // When true (school notifyScope includes course-wide) and a session is
    // present, the popup offers a "this date / any available date" choice.
    canJoinAnyDate?: boolean;
    // Other upcoming dates of this course that still have space (cross-sell).
    alternativeDates?: AlternativeDate[];
    // Called when the customer picks an alternative date to book instead.
    onSelectAlternativeDate?: (sessionId: string) => void;
    // Other listings at this school the customer might like (cross-sell).
    similarExperiences?: SimilarExperience[];
    // Called when the customer picks a similar experience to view.
    onSelectSimilarExperience?: (slug: string) => void;
    // --- Term mode (Phase I.5) ---
    // When present the popup joins a TERM's waiting list (instead of a session).
    term?: Term | null;
    // Whether the term allows drop-in — enables a "This date" choice alongside
    // "Whole term".
    hasDropInEnabled?: boolean;
    // Whether the term can still be joined (not started, or pro-rata/drop-in).
    eligible?: boolean;
    // Whether the term still has bookable dates now. When true the term-level
    // join is hidden (book a date instead); the drop-in "this date" stays.
    bookableNow?: boolean;
    // This term's bookable future sessions — shown when `bookableNow` so the
    // customer books one instead of joining a list that would never fire.
    availableDates?: AlternativeDate[];
    // The session the customer clicked (the "this date" target for drop-in).
    selectedSessionId?: string;
    onSubmit: (params: SubmitParams) => Promise<any>;
    onClose: () => void;
  }
}

export function WaitingListPopup({
  session,
  course,
  location,
  canJoinAnyDate,
  alternativeDates,
  onSelectAlternativeDate,
  similarExperiences,
  onSelectSimilarExperience,
  term,
  hasDropInEnabled,
  eligible,
  bookableNow,
  availableDates,
  selectedSessionId,
  onSubmit,
  onClose,
}: WaitingListPopup.Props) {
  const isTerm = !!term;
  const termBookable = isTerm && !!bookableNow;
  // Term-mode waitlist options:
  // - "This term" — the term-level list, only when the term is FULL/unbookable.
  // - "This date" — the clicked full session's drop-in list (drop-in terms only).
  // - "Any term"  — the course-wide list; ALWAYS offered as the fallback so the
  //   customer can always be notified about a future term, even when this one
  //   can't be joined (already started, or still bookable).
  const canJoinTerm = isTerm && !!eligible && !termBookable;
  // "This date" only when a concrete session was clicked — without one (the
  // CoursePage picker → term path) it would submit target:"session" with no
  // sessionId and silently fall through to the whole-term list.
  const canJoinDate = isTerm && !!hasDropInEnabled && !!selectedSessionId;
  // Individual dates are only independently bookable with drop-in — never show a
  // "book a date instead" list for a pro-rata / whole-term term.
  const showAvailableDates = termBookable && !!hasDropInEnabled;
  const termHasStarted =
    isTerm && new Date(term!.startDateTime).getTime() < Date.now();
  // Course-wide ("any new …") options shown when there's no specific date/term
  // (e.g. the checkout "Can't make these dates?" callout). A termly course offers
  // "Any new term"; a course with drop-in (or any per-date listing) offers "Any
  // new date". Both join the same course-wide list — just framing for the user.
  const isTermlyCourse = !isTerm && course?.type === "termly";
  const courseWideOptions: Array<{ kind: "term" | "date"; label: string }> = [];
  if (isTermlyCourse)
    courseWideOptions.push({ kind: "term", label: "Any new term" });
  if (!isTermlyCourse || !!course?.isTermDropInEnabled)
    courseWideOptions.push({ kind: "date", label: "Any new date" });
  const [firstName, setFirstName] = useState(paramCache.firstName);
  const [lastName, setLastName] = useState(paramCache.lastName);
  const [email, setEmail] = useState(paramCache.email);
  const [phone, setPhone] = useState(paramCache.phone);
  const [numberOfGuests, setNumberOfGuests] = useState(
    paramCache.numberOfGuests.toString(),
  );
  // Term mode defaults to the whole term; session mode to the specific date (or
  // course-wide when no session). Only switchable via the UI per mode.
  const [target, setTarget] = useState<"session" | "course" | "term">(
    isTerm
      ? canJoinTerm
        ? "term"
        : canJoinDate
          ? "session"
          : "course"
      : session
        ? "session"
        : "course",
  );
  // Cosmetic sub-choice for the course-wide options ("Any new term" vs "Any new
  // date") — both submit the same course-wide list.
  const [courseWideKind, setCourseWideKind] = useState<"term" | "date">(
    isTermlyCourse ? "term" : "date",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const onChangeEmail = (value: string) => {
    setEmail(value);
    if (emailError) setEmailError(null);
  };

  const onSubmitAction = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError(null);
    setFormError(null);

    const params: WaitingListPopup.SubmitParams = {
      firstName,
      lastName,
      email: trimmedEmail,
      phone,
      // Clearable input: an empty/invalid field parses to NaN — fall back to 1
      // guest (and floor negatives) instead of sending NaN to the API (which
      // would also poison paramCache for the next open).
      numberOfGuests: Math.max(1, parseInt(numberOfGuests) || 1),
      target,
      // For a drop-in "this date" choice in term mode, the session to join.
      ...(isTerm && target === "session"
        ? { sessionId: selectedSessionId }
        : {}),
    };

    setIsLoading(true);

    try {
      await onSubmit(params);
      setIsComplete(true);
      paramCache = params;
    } catch (e: any) {
      const apiMessage =
        e?.response?.data?.message ?? e?.response?.data?.ux_message;
      if (
        e?.response?.status === 400 &&
        typeof apiMessage === "string" &&
        /email/i.test(apiMessage)
      ) {
        setEmailError(apiMessage);
      } else {
        setFormError(
          typeof apiMessage === "string" && apiMessage
            ? apiMessage
            : "Something went wrong. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isComplete) {
    return (
      <div className={styles.overlayWrap()}>
        <div className={styles.overlay()} onClick={onClose} />
        <div className={styles.container()}>
          <p className={styles.completeText()}>
            You have been added to the waiting list! You'll receive an email if
            any spaces become available{" "}
            {target === "term"
              ? "for this term"
              : target === "course"
                ? isTerm || courseWideKind === "term"
                  ? "for any new term"
                  : "for any new date"
                : "for this date"}
            .
          </p>
          <div className={styles.completeButton()}>
            {/* @ts-ignore */}
            <PrimaryButton
              onClick={onClose}
              color="emeraldish"
              width="auto"
              text="Done"
            />
          </div>
        </div>
      </div>
    );
  }

  // In term mode, the drop-in "this date" session the customer clicked — shown
  // in the card and submitted when target is "session".
  const selectedSession =
    isTerm && selectedSessionId
      ? (term!.sessions ?? []).find(
          (s: any) => String(s._id) === String(selectedSessionId)
        )
      : null;

  // The context card. Term mode shows the whole-term span, or — when "This date"
  // is chosen — the specific session's date. Session mode shows the chosen date
  // (or "any available date").
  const card = isTerm ? (
    <div className={styles.info()}>
      {/* @ts-ignore */}
      <div className={styles.box()}>
        <h4 className={styles.title()}>{term!.title}</h4>
        {target === "session" && selectedSession ? (
          <div>
            {Session__DisplayDateTimeOffset(selectedSession, course.timezone)}
          </div>
        ) : target === "course" ? (
          <div>Any new term</div>
        ) : (
          <>
            <div>
              {term!.name ? `${term!.name} · ` : ""}
              {Session__DisplayDate(
                { startDateTime: term!.startDateTime, isOnline: false },
                course.timezone
              )}
              {" – "}
              {Session__DisplayDate(
                { startDateTime: term!.endDateTime, isOnline: false },
                course.timezone
              )}
            </div>
            <div>{term!.numberOfSessions} sessions</div>
          </>
        )}
      </div>
    </div>
  ) : session ? (
    <div className={styles.info()}>
      {/* @ts-ignore */}
      <div className={styles.box()}>
        <h4 className={styles.title()}>{course.title}</h4>
        <div>
          {target === "course"
            ? "Any new date"
            : Session__DisplayDateTimeOffset(session, course.timezone)}
        </div>
        {location && target === "session" && <span>{location.area}</span>}
      </div>
    </div>
  ) : (
    // Pure course-wide (no specific date/term — e.g. a full course's "Get in
    // touch" or the no-bookable-options entry): show an "any date" card so it's
    // clear what's being joined, mirroring the toggle's course-wide card.
    <div className={styles.info()}>
      {/* @ts-ignore */}
      <div className={styles.box()}>
        <h4 className={styles.title()}>{course.title}</h4>
        <div>{courseWideKind === "term" ? "Any new term" : "Any new date"}</div>
      </div>
    </div>
  );

  return (
    <div className={styles.overlayWrap()}>
      <div className={styles.overlay()} onClick={onClose} />
      <div className={styles.container()}>
        <button className={styles.closeButton()} onClick={onClose}>
          {/* @ts-ignore */}
          <Icon icon={Cross} width={20} height={20} />
        </button>
        <div className={styles.intro()}>
          <h3 className={styles.introTitle()}>Join Waiting List</h3>
          <p className={styles.introText()}>
            {isTerm
              ? "Fill out the form below to join the waiting list for this term. After joining, you'll be notified if any spaces become available."
              : "Fill out the form below to join the waiting list for this class. After joining, you'll be notified if any spaces become available."}
          </p>
        </div>

          <form onSubmit={onSubmitAction} className={styles.form()}>
            {isTerm && !canJoinTerm && !canJoinDate && termHasStarted && (
              <p className={styles.introText()}>
                This term has already started, so it can't be joined — add your
                details below to be notified about upcoming terms.
              </p>
            )}
            {isTerm && (canJoinTerm || canJoinDate) && (
              <div className={styles.choice()}>
                <p className={styles.choiceLabel()}>Join the waiting list for</p>
                <div className={styles.choiceButtons()}>
                  {canJoinTerm && (
                    <button
                      type="button"
                      className={styles.choiceButton({ active: target === "term" })}
                      onClick={() => setTarget("term")}
                    >
                      This term
                    </button>
                  )}
                  {canJoinDate && (
                    <button
                      type="button"
                      className={styles.choiceButton({
                        active: target === "session",
                      })}
                      onClick={() => setTarget("session")}
                    >
                      This date
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.choiceButton({
                      active: target === "course",
                    })}
                    onClick={() => setTarget("course")}
                  >
                    Any new term
                  </button>
                </div>
              </div>
            )}

            {!isTerm && session && canJoinAnyDate && (
              <div className={styles.choice()}>
                <p className={styles.choiceLabel()}>Notify me about</p>
                <div className={styles.choiceButtons()}>
                  <button
                    type="button"
                    className={styles.choiceButton({
                      active: target === "session",
                    })}
                    onClick={() => setTarget("session")}
                  >
                    This date
                  </button>
                  <button
                    type="button"
                    className={styles.choiceButton({
                      active: target === "course",
                    })}
                    onClick={() => setTarget("course")}
                  >
                    Any new date
                  </button>
                </div>
              </div>
            )}

            {!isTerm && !session && courseWideOptions.length > 1 && (
              <div className={styles.choice()}>
                <p className={styles.choiceLabel()}>Notify me about</p>
                <div className={styles.choiceButtons()}>
                  {courseWideOptions.map(opt => (
                    <button
                      key={opt.kind}
                      type="button"
                      className={styles.choiceButton({
                        active: courseWideKind === opt.kind,
                      })}
                      onClick={() => setCourseWideKind(opt.kind)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {card}

            <div className={styles.fields()}>
              <div>
                {/* @ts-ignore */}
                <Label label="First Name*" size="small">
                  <TextInput
                    onChange={setFirstName}
                    value={firstName}
                    required
                    autoFocus
                  />
                </Label>
              </div>
              <div>
                {/* @ts-ignore */}
                <Label label="Last Name*" size="small">
                  <TextInput onChange={setLastName} value={lastName} required />
                </Label>
              </div>
              <div>
                {/* @ts-ignore */}
                <Label label="Email*" size="small">
                  <TextInput
                    onChange={onChangeEmail}
                    value={email}
                    type="email"
                    required
                  />
                </Label>
                {emailError && (
                  <p className={styles.errorText()}>{emailError}</p>
                )}
              </div>
              <div>
                {/* @ts-ignore */}
                <Label label="Phone Number" size="small">
                  <TextInput onChange={setPhone} value={phone} type="tel" />
                </Label>
              </div>
              <div>
                {/* @ts-ignore */}
                <Label label="Number of Guests" size="small">
                  <TextInput
                    onChange={setNumberOfGuests}
                    value={numberOfGuests}
                    type="number"
                  />
                </Label>
              </div>
            </div>
            {formError && <p className={styles.errorText()}>{formError}</p>}
            <div className={styles.buttonContainer()}>
              {/* @ts-ignore */}
              <PrimaryButton
                type="submit"
                onClick={() => {}}
                color="emeraldish"
                width="auto"
                text="Join Waiting List"
              />
            </div>

            {isLoading && (
              <div className={styles.formOverlay()}>
                <div className={styles.loaderSpinner()} />
              </div>
            )}
          </form>

        {isTerm && showAvailableDates && onSelectAlternativeDate && (
          <CrossSellList
            title="Available dates for this term"
            intro="These still have space — book one instead of waiting."
            items={(availableDates ?? []).map(d => ({
              key: d.sessionId,
              label: Session__DisplayDateTimeOffset(d, course.timezone),
              meta: (
                <span className={styles.alternativeSpaces()}>
                  {d.availability} {d.availability === 1 ? "space" : "spaces"}
                </span>
              ),
              onClick: () => onSelectAlternativeDate(d.sessionId)
            }))}
          />
        )}

        {onSelectAlternativeDate && (
          <CrossSellList
            title="Other dates for this class"
            intro="These still have space — book one now instead of waiting."
            items={(alternativeDates ?? []).map(alt => ({
              key: alt.sessionId,
              label: Session__DisplayDateTimeOffset(alt, course.timezone),
              meta: (
                <span className={styles.alternativeSpaces()}>
                  {alt.availability}{" "}
                  {alt.availability === 1 ? "space" : "spaces"}
                </span>
              ),
              onClick: () => onSelectAlternativeDate(alt.sessionId)
            }))}
          />
        )}

        {onSelectSimilarExperience && (
          <CrossSellList
            title="You might also like"
            intro="Other experiences at this school with availability."
            items={(similarExperiences ?? []).map(exp => ({
              key: exp.courseId,
              label: exp.title,
              meta: exp.nextAvailableDate ? (
                <span className={styles.similarNextDate()}>
                  {Session__DisplayDate(
                    { startDateTime: exp.nextAvailableDate, isOnline: false },
                    course.timezone
                  )}
                </span>
              ) : undefined,
              onClick: () => onSelectSimilarExperience(exp.slug)
            }))}
          />
        )}
      </div>
    </div>
  );
}
